import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Cloud, Sun, CloudRain, Thermometer, Droplets, Wind, AlertTriangle,
  Calendar, TrendingUp, Loader2, MapPin, RefreshCw, ChevronRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const WeatherPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState({ lat: null, lon: null, name: "" });
  const [forecast, setForecast] = useState(null);
  const [historical, setHistorical] = useState(null);
  const [activeTab, setActiveTab] = useState("current");

  const fetchWeather = useCallback(async (lat, lon) => {
    setLoading(true);
    try {
      const [forecastRes, historicalRes] = await Promise.all([
        axios.get(`${API}/weather/forecast?lat=${lat}&lon=${lon}`),
        axios.get(`${API}/weather/historical?lat=${lat}&lon=${lon}`)
      ]);
      setForecast(forecastRes.data);
      setHistorical(historicalRes.data);
    } catch (error) {
      toast.error("Failed to fetch weather data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            name: "Your Location"
          });
        },
        () => {
          // Default to a location if geolocation fails
          setLocation({ lat: 40.7128, lon: -74.0060, name: "New York" });
        }
      );
    }
  }, []);

  useEffect(() => {
    if (location.lat && location.lon) {
      fetchWeather(location.lat, location.lon);
    }
  }, [location.lat, location.lon, fetchWeather]);

  const getWeatherIcon = (code) => {
    if (code === 0 || code === 1) return <Sun className="w-6 h-6 text-yellow-500" />;
    if (code >= 2 && code <= 3) return <Cloud className="w-6 h-6 text-gray-500" />;
    if (code >= 51 && code <= 82) return <CloudRain className="w-6 h-6 text-blue-500" />;
    return <Cloud className="w-6 h-6 text-gray-400" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}>
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Cloud className="w-6 h-6 text-sky-500" />
                Weather Forecast
              </h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {location.name || `${location.lat?.toFixed(2)}, ${location.lon?.toFixed(2)}`}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchWeather} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: "current", label: "Current & 7-Day", icon: Sun },
            { id: "hourly", label: "48-Hour Forecast", icon: Calendar },
            { id: "historical", label: "Historical Trends", icon: TrendingUp },
            { id: "advisories", label: "Garden Advisories", icon: AlertTriangle }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 pb-12">
        {loading && !forecast ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Current & 7-Day Tab */}
            {activeTab === "current" && forecast && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Current Weather Card */}
                <Card className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sky-100 text-sm">Current Weather</p>
                        <p className="text-5xl font-bold">{forecast.current?.temp}°C</p>
                        <p className="text-lg mt-1">{forecast.current?.weather_description}</p>
                        <p className="text-sky-100 text-sm mt-2">{forecast.current?.translation}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-sky-100">
                          <Droplets className="w-4 h-4" />
                          {forecast.current?.humidity}% humidity
                        </div>
                        <div className="flex items-center gap-2 text-sky-100 mt-1">
                          <Wind className="w-4 h-4" />
                          {forecast.current?.wind_speed} km/h
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tomorrow's Forecast Translation */}
                {forecast.tomorrow_forecast && (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <p className="font-medium text-amber-900">{forecast.tomorrow_forecast}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Dry Spell Alert */}
                {forecast.dry_spell_alert && (
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4 flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-red-900">{forecast.dry_spell_alert.message}</p>
                        <p className="text-sm text-red-700 mt-1">
                          <strong>Translation:</strong> {forecast.dry_spell_alert.translation}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* 7-Day Forecast */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">7-Day Forecast</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-2">
                      {forecast.daily_7day?.map((day, idx) => (
                        <div key={idx} className="text-center p-3 rounded-lg bg-muted/50">
                          <p className="text-sm text-muted-foreground">
                            {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                          </p>
                          {getWeatherIcon(day.weather_code)}
                          <p className="font-bold">{Math.round(day.temp_max)}°</p>
                          <p className="text-sm text-muted-foreground">{Math.round(day.temp_min)}°</p>
                          <p className="text-xs text-blue-500">{day.precip_prob}%</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* 48-Hour Tab */}
            {activeTab === "hourly" && forecast && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Next 48 Hours</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <div className="flex gap-3 min-w-max pb-4">
                        {forecast.hourly_48h?.map((hour, idx) => (
                          <div key={idx} className="text-center p-3 rounded-lg bg-muted/50 min-w-[80px]">
                            <p className="text-xs text-muted-foreground">
                              {new Date(hour.time).toLocaleTimeString('en', { hour: 'numeric' })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(hour.time).toLocaleDateString('en', { weekday: 'short' })}
                            </p>
                            {getWeatherIcon(hour.weather_code)}
                            <p className="font-bold">{Math.round(hour.temp)}°</p>
                            <p className="text-xs text-blue-500 flex items-center justify-center gap-1">
                              <CloudRain className="w-3 h-3" />
                              {hour.precip_prob}%
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Historical Trends Tab */}
            {activeTab === "historical" && historical && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Year-over-Year Comparison */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">5-Year Historical Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-5 gap-4">
                      {Object.entries(historical.year_data || {}).map(([year, data]) => (
                        <div key={year} className={`p-4 rounded-lg ${historical.drought_years?.includes(parseInt(year)) ? 'bg-amber-50 border border-amber-200' : 'bg-muted/50'}`}>
                          <p className="font-bold text-lg">{year}</p>
                          <p className="text-sm">Avg High: {data.avg_temp_max?.toFixed(1)}°C</p>
                          <p className="text-sm">Total Rain: {data.total_rainfall?.toFixed(0)}mm</p>
                          <p className="text-sm">{data.rainy_days} rainy days</p>
                          {historical.drought_years?.includes(parseInt(year)) && (
                            <span className="text-xs text-amber-700 font-medium">Drought year</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Comparisons */}
                {historical.comparisons?.length > 0 && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-lg text-blue-900">Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {historical.comparisons.map((comp, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-blue-800">
                            <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {comp}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Planting Advice */}
                {historical.planting_advice?.length > 0 && (
                  <Card className="bg-green-50 border-green-200">
                    <CardHeader>
                      <CardTitle className="text-lg text-green-900">Planting Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {historical.planting_advice.map((advice, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-green-800">
                            <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {advice}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Monthly Averages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Monthly Averages (5-Year)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                      {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((month, idx) => {
                        const monthKey = String(idx + 1).padStart(2, '0');
                        const data = historical.monthly_averages?.[monthKey];
                        return (
                          <div key={month} className="text-center p-2 rounded-lg bg-muted/50">
                            <p className="text-xs font-medium">{month}</p>
                            <p className="text-sm font-bold">{data?.avg_temp?.toFixed(0) || '-'}°</p>
                            <p className="text-xs text-blue-500">{data?.avg_rainfall?.toFixed(0) || '-'}mm</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Advisories Tab */}
            {activeTab === "advisories" && forecast && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Garden Advisories
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {forecast.advisories?.length > 0 ? (
                      forecast.advisories.map((advisory, idx) => (
                        <div key={idx} className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                          <p className="font-medium text-amber-900">{advisory.raw}</p>
                          <p className="text-sm text-amber-700 mt-2">
                            <strong>Translation:</strong> {advisory.translation}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                        <p className="text-green-800">✓ No weather concerns for your garden today. Good conditions for gardening!</p>
                      </div>
                    )}

                    {/* General tips based on conditions */}
                    <div className="mt-6">
                      <h4 className="font-medium mb-3">General Tips for Today</h4>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        {forecast.current?.humidity > 60 && (
                          <li className="flex items-start gap-2">
                            <Droplets className="w-4 h-4 mt-0.5 text-blue-500" />
                            High humidity ({forecast.current?.humidity}%) - Watch for fungal diseases. Avoid watering foliage.
                          </li>
                        )}
                        {forecast.current?.temp > 30 && (
                          <li className="flex items-start gap-2">
                            <Thermometer className="w-4 h-4 mt-0.5 text-red-500" />
                            Hot day ahead - Water early morning. Consider shade cloth for sensitive plants.
                          </li>
                        )}
                        {forecast.current?.wind_speed > 20 && (
                          <li className="flex items-start gap-2">
                            <Wind className="w-4 h-4 mt-0.5 text-gray-500" />
                            Windy conditions - Stake tall plants. Delay spraying pesticides.
                          </li>
                        )}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default WeatherPage;
